import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { productService } from '../services/productService';
import { DeliveryEstimateProps } from '../types/components';
import {
  DeliveryEstimation,
  DeliveryEstimationRequest,
} from '../types/product';
import LoadingSpinner from './LoadingSpinner';

const DeliveryEstimateContainer = styled.div.withConfig({
  shouldForwardProp: prop => prop !== 'compact',
})<{ compact?: boolean }>`
  background: #f9fafb;
  border-radius: 8px;
  padding: ${props => (props.compact ? '12px' : '16px')};
  margin: ${props => (props.compact ? '8px 0' : '16px 0')};
  border-left: 4px solid #2563eb;
`;

const EstimateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const EstimateTitle = styled.h4`
  margin: 0;
  color: #2563eb;
  font-size: 16px;
  font-weight: 600;
`;

const StockStatus = styled.span.withConfig({
  shouldForwardProp: prop => prop !== 'isInStock',
})<{ isInStock: boolean }>`
  background: ${props => (props.isInStock ? '#e8f5e8' : '#fff3cd')};
  color: ${props => (props.isInStock ? '#2e7d2e' : '#856404')};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
`;

const DeliveryTime = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const DeliveryDate = styled.div`
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 12px;
`;

const Timeline = styled.div`
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
`;

const TimelineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const TimelineStage = styled.div`
  font-weight: 500;
  color: #1f2937;
`;

const TimelineDays = styled.div`
  font-weight: 600;
  color: #2563eb;
  min-width: 60px;
  text-align: right;
`;

const TimelineDescription = styled.div`
  font-size: 12px;
  color: #4b5563;
  margin-top: 2px;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  text-align: center;
  padding: 12px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const DeliveryEstimate: React.FC<DeliveryEstimateProps> = ({
  product,
  selectedMaterialId,
  selectedExtraIds = [],
  quantity = 1,
  showTimeline = false,
  compact = false,
}) => {
  const [estimate, setEstimate] = useState<DeliveryEstimation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestRequestRef = useRef(0);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    // Debounce rapid updates to reduce flicker
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const requestId = ++latestRequestRef.current;
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const request: DeliveryEstimationRequest = {
          productId: product.id,
          selectedMaterialId,
          selectedExtraIds,
          quantity,
        };
        const estimateData = await productService.estimateDelivery(request);
        if (latestRequestRef.current === requestId) {
          setEstimate(estimateData);
        }
      } catch (err: any) {
        // Keep existing estimate to avoid flicker; show inline error message
        if (latestRequestRef.current === requestId) {
          setError('Unable to update delivery estimate');
        }
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [product.id, selectedMaterialId, selectedExtraIds, quantity]);

  return (
    <DeliveryEstimateContainer compact={compact}>
      <EstimateHeader>
        <EstimateTitle>Delivery Estimate</EstimateTitle>
        {loading && (
          <LoadingContainer>
            <LoadingSpinner size='sm' />
          </LoadingContainer>
        )}
        {estimate && (
          <StockStatus isInStock={estimate.isInStock}>
            {estimate.stockStatus}
          </StockStatus>
        )}
      </EstimateHeader>

      {error && !estimate && (
        <ErrorMessage>
          {error || 'Unable to calculate delivery estimate'}
        </ErrorMessage>
      )}

      {estimate && (
        <>
          <DeliveryTime>{estimate.deliveryEstimateDisplay}</DeliveryTime>

          {estimate.estimatedDeliveryDate && (
            <DeliveryDate>
              Estimated delivery:{' '}
              {new Date(estimate.estimatedDeliveryDate).toLocaleDateString(
                'en-US',
                {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }
              )}
            </DeliveryDate>
          )}

          {showTimeline &&
            estimate.processingTimeline &&
            estimate.processingTimeline.length > 0 && (
              <Timeline>
                {estimate.processingTimeline.map((item, index) => (
                  <TimelineItem key={index}>
                    <div>
                      <TimelineStage>{item.stage}</TimelineStage>
                      <TimelineDescription>
                        {item.description}
                      </TimelineDescription>
                    </div>
                    <TimelineDays>
                      {item.days === 0
                        ? 'Ready'
                        : item.days === 1
                          ? '1 day'
                          : `${item.days} days`}
                    </TimelineDays>
                  </TimelineItem>
                ))}
              </Timeline>
            )}
        </>
      )}
    </DeliveryEstimateContainer>
  );
};

export default DeliveryEstimate;
